import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import {
  Users,
  Plus,
  Search,
  Edit,
  Shield,
  ChevronLeft,
  ChevronRight,
  X,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v1`;

interface User {
  id: string;
  email: string;
  name: string;
  roles: string[];
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
}

const ALL_ROLES = [
  'Admin',
  'Marketing Head',
  'Marketing Executive',
  'Creative Agency',
  'ReadOnly',
];

const UsersPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [searchEmail, setSearchEmail] = useState('');
  const [filterRole, setFilterRole] = useState('');
  
  // Modals
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [rolesModalOpen, setRolesModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  
  // Form states
  const [createForm, setCreateForm] = useState({
    name: '',
    email: '',
    password: '',
    roles: ['ReadOnly'] as string[],
  });
  const [editForm, setEditForm] = useState({ name: '' });
  const [rolesForm, setRolesForm] = useState<string[]>([]);

  useEffect(() => {
    fetchUsers();
    fetchCount();
  }, [page, searchEmail, filterRole]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params: any = { page, limit };
      if (searchEmail) params.email = searchEmail;
      if (filterRole) params.role = filterRole;
      
      const response = await axios.get(`${API}/users`, { params });
      setUsers(response.data);
    } catch (error: any) {
      toast.error('Failed to load users');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCount = async () => {
    try {
      const params: any = {};
      if (searchEmail) params.email = searchEmail;
      if (filterRole) params.role = filterRole;
      
      const response = await axios.get(`${API}/users/count`, { params });
      setTotalCount(response.data.count);
    } catch (error) {
      console.error(error);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/users`, createForm);
      toast.success('User created successfully!');
      setCreateModalOpen(false);
      setCreateForm({ name: '', email: '', password: '', roles: ['ReadOnly'] });
      fetchUsers();
      fetchCount();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    
    try {
      await axios.patch(`${API}/users/${selectedUser.id}`, { name: editForm.name });
      toast.success('User updated successfully!');
      setEditModalOpen(false);
      fetchUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleUpdateRoles = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    
    try {
      await axios.patch(`${API}/users/${selectedUser.id}/roles`, { roles: rolesForm });
      toast.success('User roles updated successfully!');
      setRolesModalOpen(false);
      fetchUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update roles');
    }
  };

  const handleToggleActive = async (user: User) => {
    try {
      await axios.patch(`${API}/users/${user.id}`, { is_active: !user.is_active });
      toast.success(`User ${!user.is_active ? 'activated' : 'deactivated'} successfully!`);
      fetchUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    }
  };

  const openEditModal = (user: User) => {
    setSelectedUser(user);
    setEditForm({ name: user.name });
    setEditModalOpen(true);
  };

  const openRolesModal = (user: User) => {
    setSelectedUser(user);
    setRolesForm(user.roles);
    setRolesModalOpen(true);
  };

  const toggleRoleInForm = (role: string) => {
    if (rolesForm.includes(role)) {
      setRolesForm(rolesForm.filter((r) => r !== role));
    } else {
      setRolesForm([...rolesForm, role]);
    }
  };

  const toggleCreateRole = (role: string) => {
    if (createForm.roles.includes(role)) {
      setCreateForm({ ...createForm, roles: createForm.roles.filter((r) => r !== role) });
    } else {
      setCreateForm({ ...createForm, roles: [...createForm.roles, role] });
    }
  };

  const totalPages = Math.ceil(totalCount / limit);

  return (
    <div data-testid="users-page">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Header */}
        <div className="p-3 sm:p-4 lg:p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800" style={{ fontFamily: 'Inter, sans-serif' }}>
                User Management
              </h2>
              <p className="text-sm sm:text-base text-gray-600 mt-1">Manage users and their roles</p>
            </div>
            <button
              onClick={() => setCreateModalOpen(true)}
              className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition min-h-[44px]"
              data-testid="create-user-btn"
            >
              <Plus className="mr-2" size={16} />
              Create User
            </button>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Search by email..."
                  value={searchEmail}
                  onChange={(e) => {
                    setSearchEmail(e.target.value);
                    setPage(1);
                  }}
                  className="w-full pl-10 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[44px]"
                  data-testid="search-email-input"
                />
              </div>
            </div>
            <select
              value={filterRole}
              onChange={(e) => {
                setFilterRole(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[44px]"
              data-testid="filter-role-select"
            >
              <option value="">All Roles</option>
              {ALL_ROLES.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Roles
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    <Users className="mx-auto mb-2 text-gray-400" size={32} />
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 transition">
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-gray-900">{user.name}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {user.roles.map((role) => (
                          <span
                            key={role}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                          >
                            {role}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleToggleActive(user)}
                          disabled={user.id === currentUser?.id}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            user.is_active ? 'bg-green-600' : 'bg-gray-200'
                          } ${user.id === currentUser?.id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                          data-testid={`toggle-active-${user.email}`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              user.is_active ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                        <span className={`text-sm ${user.is_active ? 'text-green-600' : 'text-gray-400'}`}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {user.last_login_at
                        ? new Date(user.last_login_at).toLocaleDateString()
                        : 'Never'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => openEditModal(user)}
                          className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                          title="Edit user"
                          data-testid={`edit-user-${user.email}`}
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => openRolesModal(user)}
                          className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition"
                          title="Manage roles"
                          data-testid={`roles-user-${user.email}`}
                        >
                          <Shield size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <Users className="mx-auto mb-2 text-gray-400" size={32} />
              <p className="text-gray-500">No users found</p>
            </div>
          ) : (
            <div className="space-y-3 p-3">
              {users.map((user) => (
                <div key={user.id} className="border border-gray-200 rounded-lg p-4 bg-white">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{user.name}</h3>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                    <button
                      onClick={() => handleToggleActive(user)}
                      disabled={user.id === currentUser?.id}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        user.is_active ? 'bg-green-600' : 'bg-gray-200'
                      } ${user.id === currentUser?.id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      data-testid={`toggle-active-${user.email}`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          user.is_active ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 mb-1">Roles</p>
                    <div className="flex flex-wrap gap-1">
                      {user.roles.map((role) => (
                        <span
                          key={role}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                        >
                          {role}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-sm mb-3">
                    <span className="text-gray-600">Status:</span>
                    <span className={user.is_active ? 'text-green-600 font-medium' : 'text-gray-400'}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-sm mb-3">
                    <span className="text-gray-600">Last Login:</span>
                    <span className="text-gray-900">
                      {user.last_login_at ? new Date(user.last_login_at).toLocaleDateString() : 'Never'}
                    </span>
                  </div>

                  <div className="flex gap-2 pt-3 border-t border-gray-200">
                    <button
                      onClick={() => openEditModal(user)}
                      className="flex-1 min-h-[44px] px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition font-medium"
                      data-testid={`edit-user-${user.email}`}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => openRolesModal(user)}
                      className="flex-1 min-h-[44px] px-4 py-2 text-purple-600 border border-purple-600 rounded-lg hover:bg-purple-50 transition font-medium"
                      data-testid={`roles-user-${user.email}`}
                    >
                      Roles
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-3 sm:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-xs sm:text-sm text-gray-700 order-2 sm:order-1">
              Showing <span className="font-medium">{(page - 1) * limit + 1}</span> to{' '}
              <span className="font-medium">{Math.min(page * limit, totalCount)}</span> of{' '}
              <span className="font-medium">{totalCount}</span> users
            </div>
            <div className="flex space-x-2 order-1 sm:order-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="inline-flex items-center p-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition min-h-[44px] min-w-[44px] justify-center"
              >
                <ChevronLeft size={20} />
              </button>
              <div className="flex items-center px-4 text-sm font-medium text-gray-700">
                Page {page} of {totalPages}
              </div>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
                className="inline-flex items-center p-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition min-h-[44px] min-w-[44px] justify-center"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {createModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="create-user-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800">Create New User</h2>
              <button
                onClick={() => setCreateModalOpen(false)}
                className="p-1 rounded-lg hover:bg-gray-100 transition"
              >
                <X size={20} />
              </button>
            </div>
          <form onSubmit={handleCreateUser} className="p-6 space-y-4">
            <div>
              <label htmlFor="create-name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                id="create-name"
                type="text"
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                required
                className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="create-name-input"
              />
            </div>
            <div>
              <label htmlFor="create-email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                id="create-email"
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                required
                className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="create-email-input"
              />
            </div>
            <div>
              <label htmlFor="create-password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                id="create-password"
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                required
                minLength={8}
                placeholder="Minimum 8 characters"
                className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="create-password-input"
              />
              <p className="mt-1 text-xs text-gray-500">User will use this password to login</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Roles</label>
              <div className="space-y-2 mt-2">
                {ALL_ROLES.map((role) => (
                  <label key={role} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={createForm.roles.includes(role)}
                      onChange={() => toggleCreateRole(role)}
                      className="rounded"
                    />
                    <span className="text-sm">{role}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex justify-end space-x-2 px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-lg">
              <button
                type="button"
                onClick={() => setCreateModalOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
                data-testid="create-user-submit"
              >
                Create User
              </button>
            </div>
          </form>
        </div>
      </div>
      )}

      {/* Edit User Modal */}
      {editModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800">Edit User</h2>
              <button
                onClick={() => setEditModalOpen(false)}
                className="p-1 rounded-lg hover:bg-gray-100 transition"
              >
                <X size={20} />
              </button>
            </div>
          <form onSubmit={handleUpdateUser} className="p-6 space-y-4">
            <div>
              <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                id="edit-name"
                type="text"
                value={editForm.name}
                onChange={(e) => setEditForm({ name: e.target.value })}
                required
                className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="edit-name-input"
              />
            </div>
            <div className="flex justify-end space-x-2 px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-lg">
              <button
                type="button"
                onClick={() => setEditModalOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
              >
                Save Changes
              </button>
            </div>
          </form>
        </div>
      </div>
      )}

      {/* Roles Modal */}
      {rolesModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="roles-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800">Manage User Roles</h2>
              <button
                onClick={() => setRolesModalOpen(false)}
                className="p-1 rounded-lg hover:bg-gray-100 transition"
              >
                <X size={20} />
              </button>
            </div>
          <form onSubmit={handleUpdateRoles} className="p-6 space-y-4">
            <div>
              <p className="text-sm text-gray-600 mb-4">Select roles for {selectedUser?.name}</p>
              <div className="space-y-2">
                {ALL_ROLES.map((role) => (
                  <label key={role} className="flex items-center space-x-2 cursor-pointer p-2 hover:bg-gray-50 rounded">
                    <input
                      type="checkbox"
                      checked={rolesForm.includes(role)}
                      onChange={() => toggleRoleInForm(role)}
                      className="rounded"
                      data-testid={`role-checkbox-${role}`}
                    />
                    <span className="text-sm font-medium">{role}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex justify-end space-x-2 px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-lg">
              <button
                type="button"
                onClick={() => setRolesModalOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={rolesForm.length === 0}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="update-roles-submit"
              >
                Update Roles
              </button>
            </div>
          </form>
        </div>
      </div>
      )}
    </div>
  );
};

export default UsersPage;